import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  TextField,
  MenuItem,
  Button,
  FormControlLabel,
  Checkbox,
  Paper,
  Alert,
} from '@mui/material';
import {
  Today,
  Login as LoginIcon,
  Print,
} from '@mui/icons-material';
import { getAllBookings } from '../services/bookingService';
import { getAllRooms } from '../services/roomService';
import {
  getPublicBookings,
  getPublicRooms,
} from '../services/publicService';
import {
  format,
  addDays,
  addWeeks,
  subDays,
  subWeeks,
  startOfWeek,
} from 'date-fns';

// Time slots from 08:00 to 17:30 in 30-min increments
const TIME_SLOTS = [];
for (let h = 8; h <= 17; h++) {
  TIME_SLOTS.push(`${String(h).padStart(2, '0')}:00`);
  TIME_SLOTS.push(`${String(h).padStart(2, '0')}:30`);
}

const ROW_HEIGHT = 30; // px per 30-min slot

// Convert time string "HH:MM" to slot index
const timeToSlotIndex = (time) => {
  if (!time) return 0;
  const [h, m] = time.split(':').map(Number);
  return (h - 8) * 2 + (m >= 30 ? 1 : 0);
};

// Helper function to format session display
const getSessionLabel = (session) => {
  switch (session) {
    case 'am': return 'AM';
    case 'pm': return 'PM';
    case 'all_day': return 'All Day';
    default: return session || '-';
  }
};

function CalendarView({ readOnly = false }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentDate, setCurrentDate] = useState(() => {
    const dateParam = searchParams.get('date');
    if (dateParam) {
      const parsed = new Date(dateParam);
      if (!isNaN(parsed)) return parsed;
    }
    return new Date();
  });
  const [viewMode, setViewMode] = useState('day');
  const [selectedRoomId, setSelectedRoomId] = useState('');
  const [bookings, setBookings] = useState([]);
  const [allRooms, setAllRooms] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({
    resource_type: '', // '' = all, 'room' = rooms only, 'desk' = desks only
    includeCancelled: false,
  });

  useEffect(() => {
    loadRooms();
    if (searchParams.get('date')) {
      setSearchParams({}, { replace: true });
    }
  }, [readOnly]);

  useEffect(() => {
    loadBookings();
  }, [currentDate, viewMode, filters, selectedRoomId]);

  const loadRooms = async () => {
    try {
      const data = readOnly ? await getPublicRooms() : await getAllRooms();
      setAllRooms(data);
    } catch (error) {
      console.error('Failed to load rooms:', error);
    }
  };

  // Get the Monday of the current week
  const getWeekStart = (date) => {
    return startOfWeek(date, { weekStartsOn: 1 }); // Monday
  };

  const dateRange = useMemo(() => {
    if (viewMode === 'room_week') {
      const weekStart = getWeekStart(currentDate);
      const weekEnd = addDays(weekStart, 5); // Mon-Sat
      return { start: weekStart, end: weekEnd };
    }
    return { start: currentDate, end: currentDate };
  }, [currentDate, viewMode]);

  const loadBookings = async () => {
    setLoading(true);
    try {
      const params = {
        start_date: format(dateRange.start, 'yyyy-MM-dd'),
        end_date: format(dateRange.end, 'yyyy-MM-dd'),
      };

      if (viewMode === 'room_week' && selectedRoomId) {
        params.room_id = selectedRoomId;
      }

      if (!filters.includeCancelled) params.status = 'confirmed';

      const data = readOnly ? await getPublicBookings(params) : await getAllBookings(params);
      setBookings(data);
    } catch (error) {
      console.error('Failed to load bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handlePrevious = () => {
    if (viewMode === 'room_week') setCurrentDate(prev => subWeeks(prev, 1));
    else setCurrentDate(prev => subDays(prev, 1));
  };

  const handleNext = () => {
    if (viewMode === 'room_week') setCurrentDate(prev => addWeeks(prev, 1));
    else setCurrentDate(prev => addDays(prev, 1));
  };

  const handleToday = () => setCurrentDate(new Date());

  const handlePrintSchedule = () => {
    const NHS_BLUE = '#005EB8';
    const AM_BG    = '#E3F2FD';
    const PM_BG    = '#FFF3E0';
    const dateLabel  = format(currentDate, 'EEEE d MMMM yyyy');
    const clinicName = filters.resource_type === 'desk' ? 'Desk Bookings' : filters.resource_type === 'room' ? 'Room Bookings' : 'All Bookings';

    const source = bookings.filter(b => b.status === 'confirmed');

    const roomMap = {};
    for (const b of source) {
      if (!roomMap[b.room_name]) roomMap[b.room_name] = { am: null, pm: null, all_day: null, number: b.room_number };
      roomMap[b.room_name][b.session] = b;
    }
    const rooms = Object.keys(roomMap).sort((a, b) => {
      const nA = parseInt((roomMap[a].number?.match(/\d+/) || [0])[0]);
      const nB = parseInt((roomMap[b].number?.match(/\d+/) || [0])[0]);
      return nA !== nB ? nA - nB : a.localeCompare(b);
    });

    if (!rooms.length) return;

    const bookingCellHtml = (booking, label) => {
      if (!booking) return `<td style="border:1px solid #ccc;padding:6px 8px;background:#fafafa;width:45%"></td>`;
      const bg = booking.color ? `${booking.color}18` : (label === 'AM' ? AM_BG : PM_BG);
      return `<td style="border:1px solid #ccc;padding:6px 8px;vertical-align:top;background:${bg};width:45%">
        <div style="font-weight:bold;font-size:11px;margin-bottom:2px">${booking.specialty || '—'}</div>
        ${booking.doctor_name ? `<div style="font-size:10px;color:#333">${booking.doctor_name}</div>` : ''}
        ${booking.clinic_code ? `<div style="font-size:10px;color:#666;margin-top:1px">${booking.clinic_code}</div>` : ''}
      </td>`;
    };

    const rowsHtml = rooms.map(room => {
      const am  = roomMap[room].am;
      const pm  = roomMap[room].pm;
      const all = roomMap[room].all_day;

      const roomCell = `<td style="border:1px solid #ccc;padding:6px 8px;font-weight:bold;font-size:11px;background:#f5f5f5;vertical-align:middle;width:10%;white-space:nowrap">${room}</td>`;

      if (all) {
        // All-day booking spans both AM and PM columns
        const bg = all.color ? `${all.color}18` : '#f3e5f5';
        return `<tr>
          ${roomCell}
          <td colspan="2" style="border:1px solid #ccc;padding:6px 8px;vertical-align:top;background:${bg}">
            <span style="font-size:9px;color:#888;font-style:italic">All Day (08:30–17:30)</span><br/>
            <div style="font-weight:bold;font-size:11px;margin-top:2px">${all.specialty || '—'}</div>
            ${all.doctor_name ? `<div style="font-size:10px;color:#333">${all.doctor_name}</div>` : ''}
            ${all.clinic_code ? `<div style="font-size:10px;color:#666">${all.clinic_code}</div>` : ''}
          </td>
        </tr>`;
      }

      return `<tr>${roomCell}${bookingCellHtml(am, 'AM')}${bookingCellHtml(pm, 'PM')}</tr>`;
    }).join('');

    const printedAt = format(new Date(), 'dd/MM/yyyy HH:mm');

    const html = `<!DOCTYPE html><html><head><title>${clinicName} — ${dateLabel}</title>
      <style>
        *{box-sizing:border-box;margin:0;padding:0}
        body{font-family:Arial,sans-serif;font-size:11px}
        @page{size:A4 portrait;margin:10mm}
        table{border-collapse:collapse;width:100%}
        thead{display:table-header-group}
        tr{page-break-inside:avoid}
      </style></head><body>
      <div style="display:flex;justify-content:space-between;align-items:flex-end;margin-bottom:8px">
        <div>
          <div style="font-size:16px;font-weight:bold;color:${NHS_BLUE}">${clinicName}</div>
          <div style="font-size:12px;color:#555;margin-top:2px">${dateLabel}</div>
        </div>
        <div style="font-size:8px;color:#999;text-align:right">Cambridge University Hospitals<br/>Room Booking System</div>
      </div>
      <table>
        <thead>
          <tr>
            <th style="border:1px solid #ccc;padding:6px 8px;background:${NHS_BLUE};color:white;text-align:left;width:10%">Room</th>
            <th style="border:1px solid #ccc;padding:6px 8px;background:${AM_BG};color:#8B5CC8;text-align:center;width:45%">AM &nbsp; 08:30–12:30</th>
            <th style="border:1px solid #ccc;padding:6px 8px;background:${PM_BG};color:#E65100;text-align:center;width:45%">PM &nbsp; 13:30–17:30</th>
          </tr>
        </thead>
        <tbody>${rowsHtml}</tbody>
      </table>
      <div style="margin-top:6px;font-size:8px;color:#999;text-align:right">Printed ${printedAt}</div>
    </body></html>`;

    const win = window.open('', '_blank');
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => { win.print(); win.close(); }, 400);
  };

  const getViewTitle = () => {
    if (viewMode === 'room_week') {
      const weekStart = getWeekStart(currentDate);
      return `Week Beginning ${format(weekStart, 'EEEE d MMMM yyyy')}`;
    }
    return format(currentDate, 'EEEE dd MMMM yyyy');
  };

  // Extract numeric part from room number for sorting (e.g., "Room 10" -> 10)
  const extractRoomNumber = (roomNumber) => {
    if (!roomNumber) return 0;
    const match = roomNumber.match(/\d+/);
    return match ? parseInt(match[0], 10) : 0;
  };

  // Get rooms to display for the day view
  const getDisplayRooms = () => {
    const dateStr = format(currentDate, 'yyyy-MM-dd');
    const dayBookings = bookings.filter(b => b.booking_date === dateStr);
    const bookedRoomIds = new Set(dayBookings.map(b => b.room_id));
    return allRooms
      .filter(r => bookedRoomIds.has(r.id))
      .filter(r => !filters.resource_type || r.resource_type === filters.resource_type)
      .sort((a, b) => extractRoomNumber(a.room_number) - extractRoomNumber(b.room_number));
  };

  // Render booking block content (shared between both views)
  const renderBookingContent = (booking, isCancelled) => (
    <>
      {(booking.start_time || booking.end_time) && (
        <Typography sx={{ fontSize: '0.65rem', lineHeight: 1.2, opacity: 0.9, fontWeight: 'bold' }}>
          {booking.start_time?.substring(0, 5)}–{booking.end_time?.substring(0, 5)}
        </Typography>
      )}
      {booking.doctor_name && (
        <Typography sx={{ fontWeight: 'bold', fontSize: '0.75rem', lineHeight: 1.2 }}>
          {booking.doctor_name}
        </Typography>
      )}
      {booking.purpose && (
        <Typography sx={{ fontSize: '0.65rem', lineHeight: 1.3, fontStyle: 'italic' }}>
          {booking.purpose}
        </Typography>
      )}
      {booking.specialty && (
        <Typography sx={{ fontSize: '0.65rem', lineHeight: 1.3, opacity: 0.85 }}>
          {booking.specialty}
        </Typography>
      )}
      {isCancelled && (
        <Typography sx={{ fontSize: '0.65rem', fontWeight: 'bold', color: '#ffcdd2' }}>
          CANCELLED
        </Typography>
      )}
    </>
  );

  // Render the day view with time slots and room columns
  const renderDayView = () => {
    const dateStr = format(currentDate, 'yyyy-MM-dd');
    const dayBookings = bookings.filter(b => b.booking_date === dateStr);
    const displayRooms = getDisplayRooms();

    if (displayRooms.length === 0 && dayBookings.length === 0) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">No bookings for this day</Typography>
        </Box>
      );
    }

    const totalHeight = TIME_SLOTS.length * ROW_HEIGHT;
    const timeColWidth = 60;
    const roomColWidth = 160;

    return (
      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ display: 'flex', minWidth: timeColWidth + displayRooms.length * roomColWidth }}>
          {/* Time column header */}
          <Box sx={{ width: timeColWidth, minWidth: timeColWidth, flexShrink: 0 }} />

          {/* Room headers */}
          {displayRooms.map(room => (
            <Box
              key={room.id}
              sx={{
                width: roomColWidth,
                minWidth: roomColWidth,
                flexShrink: 0,
                textAlign: 'center',
                p: 1,
                borderBottom: '2px solid #9c27b0',
                borderLeft: '1px solid #e0e0e0',
                backgroundColor: '#f3e5f5',
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.8rem', color: '#6a1b9a' }}>
                {room.room_name}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Time grid */}
        <Box sx={{ display: 'flex', minWidth: timeColWidth + displayRooms.length * roomColWidth }}>
          {/* Time labels */}
          <Box sx={{ width: timeColWidth, minWidth: timeColWidth, flexShrink: 0, position: 'relative', height: totalHeight }}>
            {TIME_SLOTS.map((time, idx) => (
              <Box
                key={time}
                sx={{
                  position: 'absolute',
                  top: idx * ROW_HEIGHT,
                  height: ROW_HEIGHT,
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  pr: 1,
                  borderBottom: '1px solid #f0f0f0',
                  fontSize: '0.75rem',
                  color: '#666',
                  fontWeight: time.endsWith(':00') ? 'bold' : 'normal',
                }}
              >
                {time}
              </Box>
            ))}
          </Box>

          {/* Room columns */}
          {displayRooms.map(room => {
            const roomBookings = dayBookings.filter(b => b.room_id === room.id);

            return (
              <Box
                key={room.id}
                sx={{
                  width: roomColWidth,
                  minWidth: roomColWidth,
                  flexShrink: 0,
                  position: 'relative',
                  height: totalHeight,
                  borderLeft: '1px solid #e0e0e0',
                }}
              >
                {/* Grid lines */}
                {TIME_SLOTS.map((time, idx) => (
                  <Box
                    key={time}
                    sx={{
                      position: 'absolute',
                      top: idx * ROW_HEIGHT,
                      height: ROW_HEIGHT,
                      width: '100%',
                      borderBottom: time.endsWith(':00') ? '1px solid #e0e0e0' : '1px solid #f5f5f5',
                    }}
                  />
                ))}

                {/* Booking blocks */}
                {roomBookings.map(booking => {
                  const startIdx = timeToSlotIndex(booking.start_time);
                  const endIdx = timeToSlotIndex(booking.end_time);
                  const spanSlots = Math.max(endIdx - startIdx, 1);
                  const top = startIdx * ROW_HEIGHT;
                  const height = spanSlots * ROW_HEIGHT;
                  const isCancelled = booking.status === 'cancelled';
                  const bgColor = isCancelled ? '#bdbdbd' : (booking.color || '#B082E4');

                  return (
                    <Paper
                      key={booking.id}
                      elevation={1}
                      onClick={() => !readOnly && navigate(`/bookings/edit/${booking.id}`)}
                      sx={{
                        position: 'absolute',
                        top: top + 1,
                        left: 2,
                        right: 2,
                        height: height - 2,
                        backgroundColor: bgColor,
                        color: 'white',
                        opacity: isCancelled ? 0.7 : 1,
                        borderRadius: 1,
                        p: 0.75,
                        overflow: 'hidden',
                        zIndex: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        cursor: readOnly ? 'default' : 'pointer',
                        '&:hover': !readOnly ? { filter: 'brightness(0.9)' } : {},
                      }}
                    >
                      {renderBookingContent(booking, isCancelled)}
                    </Paper>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  // Render the room week view - single room, days as columns
  const renderRoomWeekView = () => {
    const weekStart = getWeekStart(currentDate);
    const days = [];
    for (let i = 0; i < 6; i++) { // Mon-Sat
      days.push(addDays(weekStart, i));
    }

    const selectedRoom = allRooms.find(r => r.id === parseInt(selectedRoomId));

    if (!selectedRoomId) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Please select a room to view its weekly schedule
          </Typography>
        </Box>
      );
    }

    const totalHeight = TIME_SLOTS.length * ROW_HEIGHT;
    const timeColWidth = 60;
    const dayColWidth = 160;

    return (
      <Box sx={{ overflowX: 'auto' }}>
        {/* Day headers */}
        <Box sx={{ display: 'flex', minWidth: timeColWidth + days.length * dayColWidth }}>
          {/* Empty corner for time column */}
          <Box sx={{ width: timeColWidth, minWidth: timeColWidth, flexShrink: 0 }} />

          {/* Day column headers */}
          {days.map(day => {
            const dateStr = format(day, 'yyyy-MM-dd');
            const isCurrentDay = format(new Date(), 'yyyy-MM-dd') === dateStr;

            return (
              <Box
                key={dateStr}
                sx={{
                  width: dayColWidth,
                  minWidth: dayColWidth,
                  flexShrink: 0,
                  textAlign: 'center',
                  p: 1,
                  borderBottom: '2px solid #9c27b0',
                  borderLeft: '1px solid #e0e0e0',
                  backgroundColor: isCurrentDay ? '#e8f5e9' : '#f3e5f5',
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: '0.8rem', color: isCurrentDay ? '#2e7d32' : '#6a1b9a' }}>
                  {format(day, 'EEE dd/MM/yyyy')}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* Time grid */}
        <Box sx={{ display: 'flex', minWidth: timeColWidth + days.length * dayColWidth }}>
          {/* Time labels */}
          <Box sx={{ width: timeColWidth, minWidth: timeColWidth, flexShrink: 0, position: 'relative', height: totalHeight }}>
            {TIME_SLOTS.map((time, idx) => (
              <Box
                key={time}
                sx={{
                  position: 'absolute',
                  top: idx * ROW_HEIGHT,
                  height: ROW_HEIGHT,
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  pr: 1,
                  borderBottom: '1px solid #f0f0f0',
                  fontSize: '0.75rem',
                  color: '#666',
                  fontWeight: time.endsWith(':00') ? 'bold' : 'normal',
                }}
              >
                {time}
              </Box>
            ))}
          </Box>

          {/* Day columns */}
          {days.map(day => {
            const dateStr = format(day, 'yyyy-MM-dd');
            const dayBookings = bookings.filter(b => b.booking_date === dateStr);

            return (
              <Box
                key={dateStr}
                sx={{
                  width: dayColWidth,
                  minWidth: dayColWidth,
                  flexShrink: 0,
                  position: 'relative',
                  height: totalHeight,
                  borderLeft: '1px solid #e0e0e0',
                }}
              >
                {/* Grid lines */}
                {TIME_SLOTS.map((time, idx) => (
                  <Box
                    key={time}
                    sx={{
                      position: 'absolute',
                      top: idx * ROW_HEIGHT,
                      height: ROW_HEIGHT,
                      width: '100%',
                      borderBottom: time.endsWith(':00') ? '1px solid #e0e0e0' : '1px solid #f5f5f5',
                    }}
                  />
                ))}

                {/* Booking blocks */}
                {dayBookings.map(booking => {
                  const startIdx = timeToSlotIndex(booking.start_time);
                  const endIdx = timeToSlotIndex(booking.end_time);
                  const spanSlots = Math.max(endIdx - startIdx, 1);
                  const top = startIdx * ROW_HEIGHT;
                  const height = spanSlots * ROW_HEIGHT;
                  const isCancelled = booking.status === 'cancelled';
                  const bgColor = isCancelled ? '#bdbdbd' : (booking.color || '#B082E4');

                  return (
                    <Paper
                      key={booking.id}
                      elevation={1}
                      onClick={() => !readOnly && navigate(`/bookings/edit/${booking.id}`)}
                      sx={{
                        position: 'absolute',
                        top: top + 1,
                        left: 2,
                        right: 2,
                        height: height - 2,
                        backgroundColor: bgColor,
                        color: 'white',
                        opacity: isCancelled ? 0.7 : 1,
                        borderRadius: 1,
                        p: 0.75,
                        overflow: 'hidden',
                        zIndex: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        cursor: readOnly ? 'default' : 'pointer',
                        '&:hover': !readOnly ? { filter: 'brightness(0.9)' } : {},
                      }}
                    >
                      {renderBookingContent(booking, isCancelled)}
                    </Paper>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  const selectedRoomName = allRooms.find(r => r.id === parseInt(selectedRoomId))?.room_name ?? '';

  return (
    <Container maxWidth={false} sx={{ maxWidth: '100%' }}>
      {readOnly && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<LoginIcon />}
              onClick={() => navigate('/login')}
            >
              Login
            </Button>
          }
        >
          You are viewing the calendar in read-only mode. Login to make bookings or edits.
        </Alert>
      )}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Calendar View {readOnly && <Typography component="span" variant="h6" color="text.secondary">(Read Only)</Typography>}
        </Typography>
      </Box>

      {/* Filters and Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              select
              label="View"
              size="small"
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="day">Daily</MenuItem>
              <MenuItem value="room_week">Weekly</MenuItem>
            </TextField>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button variant="text" size="small" onClick={handlePrevious} sx={{ minWidth: 'auto' }}>
                &laquo; Prev
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Today />}
                onClick={handleToday}
              >
                Today
              </Button>
              <Button variant="text" size="small" onClick={handleNext} sx={{ minWidth: 'auto' }}>
                Next &raquo;
              </Button>
            </Box>

            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {getViewTitle()}
            </Typography>

            {viewMode === 'room_week' && (
              <>
                <Box sx={{ flexGrow: 1 }} />
                <TextField
                  select
                  label="Show"
                  size="small"
                  value={filters.resource_type}
                  onChange={(e) => { handleFilterChange('resource_type', e.target.value); setSelectedRoomId(''); }}
                  sx={{ minWidth: 130 }}
                >
                  <MenuItem value="">Rooms &amp; Desks</MenuItem>
                  <MenuItem value="room">Rooms only</MenuItem>
                  <MenuItem value="desk">Desks only</MenuItem>
                </TextField>
                <TextField
                  select
                  label="Select Room / Desk"
                  size="small"
                  value={selectedRoomId}
                  onChange={(e) => setSelectedRoomId(e.target.value)}
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="">Select...</MenuItem>
                  {allRooms
                    .filter(r => !filters.resource_type || r.resource_type === filters.resource_type)
                    .map(room => (
                      <MenuItem key={room.id} value={room.id}>
                        {room.room_name}
                      </MenuItem>
                    ))}
                </TextField>
              </>
            )}

            {viewMode === 'day' && (
              <>
                <Box sx={{ flexGrow: 1 }} />
                <TextField
                  select
                  label="Show"
                  size="small"
                  value={filters.resource_type}
                  onChange={(e) => handleFilterChange('resource_type', e.target.value)}
                  sx={{ minWidth: 130 }}
                >
                  <MenuItem value="">Rooms &amp; Desks</MenuItem>
                  <MenuItem value="room">Rooms only</MenuItem>
                  <MenuItem value="desk">Desks only</MenuItem>
                </TextField>
              </>
            )}

            {viewMode === 'day' && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<Print />}
                onClick={handlePrintSchedule}
                disabled={bookings.filter(b => b.status === 'confirmed').length === 0}
              >
                Print
              </Button>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.includeCancelled}
                  onChange={(e) => handleFilterChange('includeCancelled', e.target.checked)}
                  size="small"
                />
              }
              label="Include Cancelled"
            />
          </Box>
        </CardContent>
      </Card>

      {/* Calendar */}
      <Card>
        {viewMode === 'room_week' && selectedRoomName && (
          <Box sx={{ p: 1.5, backgroundColor: '#f3e5f5', borderBottom: '2px solid #9c27b0', textAlign: 'right' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#6a1b9a' }}>
              {selectedRoomName}
            </Typography>
          </Box>
        )}
        <CardContent sx={{ p: 1, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography>Loading...</Typography>
            </Box>
          ) : viewMode === 'room_week' ? (
            renderRoomWeekView()
          ) : (
            renderDayView()
          )}
        </CardContent>
      </Card>
    </Container>
  );
}

export default CalendarView;
